import {
    type DynamicWidgetActionRequest,
    START_OUTREACH_MAX_SHIFT_COUNT,
} from '@apps/web/components/phoebe_chat/dynamic_widget_action';
import {
    type DynamicWidgetModel,
    type ShiftListWidgetRow,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import {
    type RenderTimelineEntry,
    type ToolExecutionEntry,
} from '@apps/web/components/phoebe_chat_view/transcript_types';

export {
    type DynamicWidgetActionRequest,
    parseDynamicWidgetActionRequest,
    START_OUTREACH_MAX_SHIFT_COUNT,
} from '@apps/web/components/phoebe_chat/dynamic_widget_action';

export type DynamicWidgetActionErrorCode =
    | 'widget_action_caregiver_count_invalid'
    | 'widget_action_caregivers_not_found'
    | 'widget_action_client_operation_id_mismatch'
    | 'widget_action_provenance_mismatch'
    | 'widget_action_shift_count_invalid'
    | 'widget_action_shifts_not_in_source'
    | 'widget_action_shifts_not_found'
    | 'widget_action_shifts_not_actionable'
    | 'widget_action_shift_status_stale';

export type DynamicWidgetActionStatus =
    | 'idle'
    | 'submitted'
    | 'awaiting_approval'
    | 'running'
    | 'succeeded'
    | 'failed'
    | 'superseded';

export type DynamicWidgetActionState = {
    readonly status: DynamicWidgetActionStatus;
    readonly action: DynamicWidgetActionRequest | null;
    readonly errorCode: DynamicWidgetActionErrorCode | null;
};

export type DynamicWidgetActionStateResolver = (
    widgetId: string,
    shiftIds: ReadonlyArray<string>,
    suggestedCaregiverIds?: ReadonlyArray<string>,
) => DynamicWidgetActionState;

type DynamicWidgetActionSendRejection = {
    readonly action: DynamicWidgetActionRequest;
    readonly error: unknown;
};

export const IDLE_DYNAMIC_WIDGET_ACTION_STATE: DynamicWidgetActionState = {
    status: 'idle',
    action: null,
    errorCode: null,
};

const OUTREACH_CREATION_TOOL_NAMES = new Set(['draft_agent_outreach']);

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getStringArray(value: unknown): Array<string> | null {
    if (
        !Array.isArray(value) ||
        !value.every((item): item is string => typeof item === 'string')
    ) {
        return null;
    }
    return value;
}

function isDynamicWidgetActionErrorCode(
    value: unknown,
): value is DynamicWidgetActionErrorCode {
    switch (value) {
        case 'widget_action_caregiver_count_invalid':
        case 'widget_action_caregivers_not_found':
        case 'widget_action_client_operation_id_mismatch':
        case 'widget_action_provenance_mismatch':
        case 'widget_action_shift_count_invalid':
        case 'widget_action_shifts_not_in_source':
        case 'widget_action_shifts_not_found':
        case 'widget_action_shifts_not_actionable':
        case 'widget_action_shift_status_stale':
            return true;
        default:
            return false;
    }
}

function coversShiftIds(
    candidateShiftIds: ReadonlyArray<string>,
    requestedShiftIds: ReadonlyArray<string>,
): boolean {
    const candidateIds = new Set(candidateShiftIds);
    return requestedShiftIds.every((shiftId) => candidateIds.has(shiftId));
}

function coversSuggestedCaregiverIds(
    action: DynamicWidgetActionRequest,
    requestedCaregiverIds: ReadonlyArray<string>,
): boolean {
    if (requestedCaregiverIds.length === 0) {
        return true;
    }
    const actionCaregiverIds = action.suggested_caregiver_ids ?? [];
    return coversShiftIds(actionCaregiverIds, requestedCaregiverIds);
}

/**
 * Creates one closed action request from the exact rows rendered by a shift
 * widget. Call this once inside a click handler so its UUID remains the
 * idempotency key for every retry of that HTTP submission.
 */
export function buildStartOutreachForShiftsAction({
    model,
    rows,
    displayText,
    suggestedCaregiverIds,
}: {
    readonly model: DynamicWidgetModel;
    readonly rows: ReadonlyArray<ShiftListWidgetRow>;
    readonly displayText: string;
    readonly suggestedCaregiverIds?: ReadonlyArray<string>;
}): DynamicWidgetActionRequest {
    const source = model.sources.find(
        (candidate) => candidate.kind === 'event_source',
    );
    if (source === undefined) {
        throw new Error('A widget action requires an event source.');
    }
    const shiftIds = Array.from(
        new Set(rows.flatMap((row) => row.occurrenceIds)),
    );
    if (shiftIds.length === 0) {
        throw new Error('Start outreach requires at least one shift.');
    }
    if (shiftIds.length > START_OUTREACH_MAX_SHIFT_COUNT) {
        throw new Error(
            `Start outreach accepts at most ${START_OUTREACH_MAX_SHIFT_COUNT} shifts.`,
        );
    }
    const shiftStatusesById: Record<string, string> = {};
    rows.forEach((row) => {
        const status = row.status;
        if (status === null) {
            throw new Error('Every selected shift requires a rendered status.');
        }
        row.occurrenceIds.forEach((shiftId) => {
            const existingStatus = shiftStatusesById[shiftId];
            if (existingStatus !== undefined && existingStatus !== status) {
                throw new Error(
                    `Shift ${shiftId} has conflicting rendered statuses.`,
                );
            }
            shiftStatusesById[shiftId] = status;
        });
    });
    return {
        action: 'start_outreach_for_shifts',
        id: globalThis.crypto.randomUUID(),
        widget_id: model.widgetId,
        source_tool_call_event_id: source.toolCallEventId,
        source_call_id: source.callId,
        shift_ids: shiftIds,
        ...(suggestedCaregiverIds === undefined
            ? {}
            : {
                  suggested_caregiver_ids: Array.from(
                      new Set(suggestedCaregiverIds),
                  ),
              }),
        expected: {
            shift_statuses_by_id: shiftStatusesById,
        },
        display_text: displayText,
    };
}

function getCommittedWidgetActions(
    entries: ReadonlyArray<RenderTimelineEntry>,
): Array<DynamicWidgetActionRequest> {
    return entries
        .filter(
            (entry) =>
                entry.kind === 'message' &&
                entry.role === 'user' &&
                !entry.id.startsWith('pending-') &&
                entry.widgetAction !== undefined,
        )
        .map((entry) =>
            entry.kind === 'message' ? (entry.widgetAction ?? null) : null,
        )
        .filter(
            (action): action is DynamicWidgetActionRequest => action !== null,
        );
}

function getToolEntries(
    entries: ReadonlyArray<RenderTimelineEntry>,
): Array<ToolExecutionEntry> {
    return entries.flatMap((entry) => {
        if (entry.kind === 'tool-execution') {
            return [entry];
        }
        if (entry.kind === 'thought') {
            return entry.tools;
        }
        return [];
    });
}

function toolOutputFailed(entry: ToolExecutionEntry): boolean {
    if (
        entry.approvalState === 'denied' ||
        (entry.error !== null && entry.error !== undefined) ||
        entry.lifecycleStatus === 'failed' ||
        entry.status === 'failed' ||
        entry.status === 'error'
    ) {
        return true;
    }
    return (
        isRecord(entry.output) &&
        (entry.output['success'] === false ||
            typeof entry.output['error'] === 'string')
    );
}

function isSuccessfulOutreachOutput(
    entry: ToolExecutionEntry,
    action: DynamicWidgetActionRequest,
): boolean {
    if (
        !OUTREACH_CREATION_TOOL_NAMES.has(entry.name) ||
        !entry.isComplete ||
        toolOutputFailed(entry) ||
        !isRecord(entry.output) ||
        typeof entry.output['outreach_id'] !== 'string' ||
        entry.output['outreach_id'].length === 0
    ) {
        return false;
    }
    const outputShiftIds = getStringArray(entry.output['shift_ids']);
    const requestedCaregiverIds = action.suggested_caregiver_ids ?? [];
    const outputCaregiverIds = getStringArray(
        entry.output['suggested_caregiver_ids'],
    );
    return (
        outputShiftIds !== null &&
        coversShiftIds(outputShiftIds, action.shift_ids) &&
        (requestedCaregiverIds.length === 0 ||
            (outputCaregiverIds !== null &&
                coversShiftIds(outputCaregiverIds, requestedCaregiverIds)))
    );
}

export function getDynamicWidgetActionErrorCode(
    error: unknown,
): DynamicWidgetActionErrorCode | null {
    let candidate: unknown = error;
    if (isRecord(candidate) && 'body' in candidate) {
        candidate = candidate['body'];
    }
    if (isRecord(candidate) && 'detail' in candidate) {
        candidate = candidate['detail'];
    }
    if (!isRecord(candidate)) {
        return null;
    }
    const code = candidate['code'];
    return isDynamicWidgetActionErrorCode(code) ? code : null;
}

/**
 * Reduces only committed transcript evidence for one action. The optional
 * rejection represents the sole pre-commit terminal case: an HTTP submission
 * the server rejected. Once its user-message event commits, transcript
 * evidence wins and the transient rejection is ignored.
 */
export function reduceDynamicWidgetActionState(
    actionId: string,
    entries: ReadonlyArray<RenderTimelineEntry>,
    sendRejection?: DynamicWidgetActionSendRejection,
): DynamicWidgetActionState {
    const committedActions = getCommittedWidgetActions(entries);
    const actionIndex = committedActions.findIndex(
        (action) => action.id === actionId,
    );
    const action = committedActions[actionIndex] ?? null;
    if (action === null) {
        if (sendRejection?.action.id !== actionId) {
            return IDLE_DYNAMIC_WIDGET_ACTION_STATE;
        }
        return {
            status: 'failed',
            action: sendRejection.action,
            errorCode: getDynamicWidgetActionErrorCode(sendRejection.error),
        };
    }

    const supersedingAction = committedActions
        .slice(actionIndex + 1)
        .find(
            (candidate) =>
                candidate.widget_id === action.widget_id &&
                coversShiftIds(candidate.shift_ids, action.shift_ids) &&
                coversSuggestedCaregiverIds(
                    candidate,
                    action.suggested_caregiver_ids ?? [],
                ),
        );
    if (supersedingAction !== undefined) {
        return {
            status: 'superseded',
            action,
            errorCode: null,
        };
    }

    const relatedTools = getToolEntries(entries).filter(
        (entry) => entry.sourceWidgetActionId === actionId,
    );
    if (
        relatedTools.some((entry) => isSuccessfulOutreachOutput(entry, action))
    ) {
        return {
            status: 'succeeded',
            action,
            errorCode: null,
        };
    }
    if (relatedTools.some(toolOutputFailed)) {
        return {
            status: 'failed',
            action,
            errorCode: null,
        };
    }
    if (relatedTools.some((entry) => entry.approvalState === 'pending')) {
        return {
            status: 'awaiting_approval',
            action,
            errorCode: null,
        };
    }
    if (
        relatedTools.some(
            (entry) =>
                (entry.approvalState === 'approved' ||
                    entry.approvalState === 'not_required') &&
                !entry.isComplete &&
                entry.output === undefined,
        )
    ) {
        return {
            status: 'running',
            action,
            errorCode: null,
        };
    }
    return {
        status: 'submitted',
        action,
        errorCode: null,
    };
}

export function getDynamicWidgetActionStateForShifts(
    widgetId: string,
    shiftIds: ReadonlyArray<string>,
    entries: ReadonlyArray<RenderTimelineEntry>,
    suggestedCaregiverIds: ReadonlyArray<string> = [],
): DynamicWidgetActionState {
    const latestAction = [...getCommittedWidgetActions(entries)]
        .reverse()
        .find(
            (action) =>
                action.widget_id === widgetId &&
                coversShiftIds(action.shift_ids, shiftIds) &&
                coversSuggestedCaregiverIds(action, suggestedCaregiverIds),
        );
    return latestAction === undefined
        ? IDLE_DYNAMIC_WIDGET_ACTION_STATE
        : reduceDynamicWidgetActionState(latestAction.id, entries);
}
